import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Lightbulb, Award, Heart, Users, ArrowRight } from "lucide-react";
import { BrandButton } from "../../components/ui/index.js";
import "./About.css";

gsap.registerPlugin(ScrollTrigger);

// ─── Data ────────────────────────────────────────────────────────────────────
const valores = [
    { icon: <Lightbulb size={18} />, title: "Innovación",  desc: "Integramos NFC, imanes y materiales avanzados en cada diseño." },
    { icon: <Award     size={18} />, title: "Calidad",     desc: "Solo materiales de primera: PLA+, PETG, filamentos técnicos." },
    { icon: <Heart     size={18} />, title: "Pasión",      desc: "Cada pieza se diseña y supervisa con atención al detalle." },
    { icon: <Users     size={18} />, title: "Comunidad",   desc: "Trabajamos codo a codo con cada cliente desde el boceto." },
];

const materiales = [
    { label: "PLA+",     desc: "Rígido, detalle fino" },
    { label: "PETG",     desc: "Resistente, semiflexible" },
    { label: "TPU Flex", desc: "Flexible y duradero" },
    { label: "NFC",      desc: "Conectividad integrada" },
    { label: "Imanes",   desc: "Funcionalidad magnética" },
    { label: "Resina UV",desc: "Ultra alta definición" },
];

// ─── Right-side floating panels ──────────────────────────────────────────────
function PanelHistoria() {
    return (
        <div className="flex flex-col items-center gap-3 text-center select-none pointer-events-none">
            <span className="text-xs uppercase tracking-[0.3em] text-primary/90 font-semibold">
                Fundada en
            </span>
            <span className="text-[9rem] font-black leading-none text-white/45">
                2023
            </span>
            <div className="w-10 h-[2px] bg-brand-red" />
            <span className="text-sm text-white/65 max-w-[180px] leading-snug">
                Nacidos de la pasión por la impresión 3D
            </span>
        </div>
    );
}

function PanelMision() {
    return (
        <div className="flex flex-col items-center gap-5 select-none pointer-events-none">
            {[
                { word: "Innovar",  size: "3.2rem" },
                { word: "Crear",    size: "2.6rem" },
                { word: "Conectar", size: "2rem"   },
            ].map(({ word, size }) => (
                <div key={word} className="flex items-center gap-4">
                    <span className="w-8 h-px bg-brand-red/70" />
                    <span
                        className="font-black tracking-tight text-white/40"
                        style={{ fontSize: size }}
                    >
                        {word}
                    </span>
                    <span className="w-8 h-px bg-brand-red/70" />
                </div>
            ))}
        </div>
    );
}

function PanelTecnologia() {
    const sizes = ["1rem", "0.85rem", "1.1rem", "0.9rem", "0.8rem", "1.05rem"];
    return (
        <div className="flex flex-wrap gap-3 max-w-[280px] justify-center select-none pointer-events-none">
            {materiales.map(({ label }, i) => (
                <span
                    key={label}
                    className="px-4 py-2 rounded-full border border-white/35 text-white/50 font-bold tracking-wide"
                    style={{ fontSize: sizes[i] }}
                >
                    {label}
                </span>
            ))}
        </div>
    );
}

function PanelValores() {
    return (
        <div className="flex flex-col items-center gap-3 select-none pointer-events-none text-center">
            <span className="text-7xl font-black text-white/30 leading-none">&laquo;</span>
            <p className="text-2xl font-light italic text-white/45 max-w-[240px] leading-snug">
                El detalle marca la diferencia
            </p>
            <span className="text-7xl font-black text-white/30 leading-none rotate-180">&laquo;</span>
        </div>
    );
}

function PanelEquipo() {
    return (
        <div className="flex items-center gap-10 select-none pointer-events-none">
            <div className="flex flex-col items-center gap-1">
                <span className="text-6xl font-black text-white/40">100%</span>
                <span className="text-xs uppercase tracking-[0.2em] text-white/55">Artesanal</span>
            </div>
            <div className="w-px h-16 bg-white/25" />
            <div className="flex flex-col items-center gap-1">
                <span className="text-6xl font-black text-brand-red/70">0%</span>
                <span className="text-xs uppercase tracking-[0.2em] text-white/55">Compromiso</span>
            </div>
        </div>
    );
}
const RIGHT_PANELS = [
    { id: "panel-historia",   Component: PanelHistoria   },
    { id: "panel-mision",     Component: PanelMision     },
    { id: "panel-tecnologia", Component: PanelTecnologia },
    { id: "panel-valores",    Component: PanelValores    },
    { id: "panel-equipo",     Component: PanelEquipo     },
];

// ─── ABOUT PAGE ──────────────────────────────────────────────────────────────
export default function About() {
    const asideRef = useRef(null);
    const rightRef = useRef(null);

    useEffect(() => {
        const aside = asideRef.current;
        const right = rightRef.current;
        if (!aside) return;

        const PANEL_IDS = ["historia", "mision", "tecnologia", "valores", "equipo"];

        // Helper: hide all panels, then show target
        const showOnly = (id) => {
            if (!right) return;
            PANEL_IDS.forEach((pid) => {
                const el = right.querySelector(`#panel-${pid}`);
                if (!el) return;
                if (pid === id) {
                    gsap.to(el, { opacity: 1, y: 0,   duration: 0.25, ease: "power2.out" });
                } else {
                    gsap.to(el, { opacity: 0, y: 0,   duration: 0.45, ease: "power2.in"  });
                }
            });
        };
        const hideAll = () => {
            if (!right) return;
            PANEL_IDS.forEach((pid) => {
                const el = right.querySelector(`#panel-${pid}`);
                if (el) gsap.to(el, { opacity: 0, duration: 0.25 });
            });
        };

        const ctx = gsap.context(() => {
            // ── Left sections slide in on scroll ─────────────────────────
            gsap.utils.toArray(".about-section", aside).forEach((el) => {
                gsap.from(el, {
                    x: -50, opacity: 0,
                    duration: 1.65, ease: "power2.out",
                    scrollTrigger: {
                        trigger: el, scroller: aside,
                        start: "top 90%",
                        toggleActions: "play none none reverse",
                    },
                });
            });

        });

        // ── Scroll handler: panels + canvas ──────────────────────────
        // Panel ranges [start, end] as fraction of total scroll
        const PANEL_RANGES = {
            historia:   [0.14, 0.28],
            mision:     [0.28, 0.44],
            tecnologia: [0.5, 0.64],
            valores:    [0.68, 0.82],
            equipo:     [0.85, 1.3],
        };

        let lastActivePanel = null;

        const onAsideScroll = () => {
            const progress = Math.min(
                aside.scrollTop / (aside.scrollHeight - aside.clientHeight),
                1
            );

            // ─ Canvas: rotationY 1.52 (canto) → -0.15 (frente), extrusion 3 → 30
            const rotY      = 1.52 - progress * 1.67;
            // const extrusion = 3 + progress * 27;
            window.dispatchEvent(new CustomEvent('customiz3d:about-scroll', {
                // detail: { rotationY: rotY, extrusion_depth: extrusion, positionX: 0 },
                detail: { rotationY: rotY, positionX: -0, positionY: 70 },
            }));

            // ─ Panels: show only the one whose range contains progress
            let activePanel = null;
            for (const [id, [start, end]] of Object.entries(PANEL_RANGES)) {
                if (progress >= start && progress < end) { activePanel = id; break; }
            }
            if (activePanel !== lastActivePanel) {
                lastActivePanel = activePanel;
                activePanel ? showOnly(activePanel) : hideAll();
            }
        };

        aside.addEventListener('scroll', onAsideScroll);

        return () => {
            ctx.revert();
            aside.removeEventListener('scroll', onAsideScroll);
        };
    }, []);

    return (
        <div className="flex h-full w-full overflow-hidden text-gray-900 dark:text-gray-200">

            {/* ── MOBILE: columna (logo peek arriba + scroll abajo) ──────── */}
            {/* ── DESKTOP: fila (aside izq + panel derecho) ─────────────── */}
            <div className="flex flex-col md:flex-row w-full md:w-[48%] h-full">

            {/* ── MOBILE logo peek — franja transparente superior ────────── */}
            <div className="md:hidden flex-shrink-0 h-[38vh] w-full relative flex flex-col justify-end px-6 pb-5 pointer-events-none">
                {/* degradado sutil para separar la zona de texto del canvas */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
                <div className="relative z-10">
                    <p className="text-[0.65rem] uppercase tracking-[0.25em] text-primary/80 font-semibold mb-1">
                        Customiz3D — Studio
                    </p>
                    <h1 className="text-3xl font-black leading-tight text-white drop-shadow">
                        Fabricamos<br />
                        <span className="text-brand-red">ideas</span> en 3D.
                    </h1>
                </div>
            </div>

            {/* ── LEFT ASIDE ─────────────────────────────────────────────── */}
            <aside
                ref={asideRef}
                className="
                    flex-1 md:w-full
                    overflow-y-auto overflow-x-hidden
                    dark:bg-base-300/80 bg-white/85 backdrop-blur-md
                    border-r border-white/10 dark:border-white/5
                "
            >
                {/* 0 · Hero — oculto en mobile (ya se muestra en la franja transparente superior) */}
                <div className="about-section hidden md:flex min-h-[65vh] flex-col justify-center px-8 md:px-12 py-16 border-b border-base-300/30">
                    <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-4">
                        Customiz3D — Studio
                    </p>
                    <h1 className="text-5xl md:text-6xl font-black leading-[1.1] dark:text-white text-gray-900 mb-6">
                        Fabricamos<br />
                        <span className="text-brand-red">ideas</span><br />
                        en 3D.
                    </h1>
                    <p className="text-base text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed">
                        Somos un estudio especializado en impresión 3D personalizada.
                        Diseñamos objetos únicos que combinan tecnología, funcionalidad
                        y estética.
                    </p>
                    <div className="mt-8 w-8 h-[2px] bg-brand-red" />
                </div>

                {/* 1 · Historia */}
                <div
                    id="trigger-historia"
                    className="about-section px-8 md:px-12 py-14 border-b border-base-300/30"
                >
                    <p className="text-xs uppercase tracking-widest text-primary/60 font-semibold mb-2">
                        01 — Historia
                    </p>
                    <h2 className="text-3xl font-extrabold dark:text-white text-gray-900 mb-5">
                        Nuestra historia
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                        Customiz3D nació en 2023 de la unión de dos pasiones: el diseño industrial
                        y la tecnología de fabricación digital. Lo que empezó como un proyecto
                        personal se convirtió rápidamente en un estudio con capacidad de producir
                        desde prototipos únicos hasta series cortas.
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                        Hoy combinamos impresión FDM, tecnología NFC e integración de imanes para
                        crear objetos que van más allá de lo decorativo: objetos que hacen cosas.
                    </p>
                </div>

                {/* 2 · Misión */}
                <div
                    id="trigger-mision"
                    className="about-section px-8 md:px-12 py-14 border-b border-base-300/30"
                >
                    <p className="text-xs uppercase tracking-widest text-primary/60 font-semibold mb-2">
                        02 — Misión
                    </p>
                    <h2 className="text-3xl font-extrabold dark:text-white text-gray-900 mb-5">
                        Nuestra misión
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-7">
                        Democratizar el acceso a objetos de diseño personalizados. Queremos que
                        cualquier persona o empresa pueda materializar su idea con calidad
                        profesional y a un precio justo.
                    </p>
                    <div className="flex flex-col gap-4">
                        {[
                            { label: "Innovar",  desc: "Integramos tecnologías emergentes en objetos cotidianos." },
                            { label: "Crear",    desc: "Cada pieza es diseñada a medida, sin plantillas genéricas." },
                            { label: "Conectar", desc: "Unimos al cliente con su objeto mediante NFC y experiencias digitales." },
                        ].map(({ label, desc }) => (
                            <div key={label} className="flex gap-4 items-start">
                                <span className="w-[3px] rounded-full bg-brand-red mt-1 self-stretch flex-shrink-0" />
                                <div>
                                    <p className="font-bold dark:text-white text-gray-900 text-sm">{label}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 3 · Tecnología */}
                <div
                    id="trigger-tecnologia"
                    className="about-section px-8 md:px-12 py-14 border-b border-base-300/30"
                >
                    <p className="text-xs uppercase tracking-widest text-primary/60 font-semibold mb-2">
                        03 — Tecnología
                    </p>
                    <h2 className="text-3xl font-extrabold dark:text-white text-gray-900 mb-5">
                        Materiales y técnicas
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-7">
                        Trabajamos con una amplia gama de filamentos y tecnologías para encontrar
                        la solución perfecta a cada proyecto.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {materiales.map(({ label, desc }) => (
                            <div
                                key={label}
                                className="glassCard rounded-xl px-3 py-2.5 flex flex-col gap-0.5"
                            >
                                <span className="font-bold text-sm dark:text-white text-gray-900">{label}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{desc}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 4 · Valores */}
                <div
                    id="trigger-valores"
                    className="about-section px-8 md:px-12 py-14 border-b border-base-300/30"
                >
                    <p className="text-xs uppercase tracking-widest text-primary/60 font-semibold mb-2">
                        04 — Valores
                    </p>
                    <h2 className="text-3xl font-extrabold dark:text-white text-gray-900 mb-6">
                        Lo que nos mueve
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {valores.map(({ icon, title, desc }) => (
                            <div key={title} className="glassCard rounded-xl p-4 flex flex-col gap-2">
                                <span className="text-primary">{icon}</span>
                                <p className="font-bold dark:text-white text-gray-900 text-sm">{title}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 5 · Equipo */}
                <div
                    id="trigger-equipo"
                    className="about-section px-8 md:px-12 py-14 pb-28"
                >
                    <p className="text-xs uppercase tracking-widest text-primary/60 font-semibold mb-2">
                        05 — Equipo
                    </p>
                    <h2 className="text-3xl font-extrabold dark:text-white text-gray-900 mb-5">
                        Quiénes somos
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                        Somos un equipo pequeño y especializado: diseñadores, ingenieros y makers
                        que creemos que los objetos deben tener personalidad propia. Cada encargo
                        lo tratamos como si fuera nuestro.
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-8">
                        ¿Tienes un proyecto en mente? Cuéntanoslo. Nos encanta el reto.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <BrandButton as="link" to="/contact" size="md">
                            Hablar con el equipo <ArrowRight size={16} />
                        </BrandButton>
                        <BrandButton as="link" to="/store" size="md" variant="outline">
                            Ver nuestros productos
                        </BrandButton>
                    </div>
                </div>
            </aside>
            </div>{/* fin columna mobile/desktop aside */}

            {/* ── RIGHT — 3D background visible + floating panels ─────────── */}
            <div
                ref={rightRef}
                className="hidden md:block flex-1 h-full relative bg-black/75"
            >
                {RIGHT_PANELS.map(({ id, Component }) => (
                    <div
                        key={id}
                        id={id}
                        className="absolute inset-0 flex items-center justify-center "
                        style={{ opacity: 0 }}
                    >
                        <Component />
                    </div>
                ))}
            </div>
        </div>
    );
}
